import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader } from 'lucide-react';
import { STORAGE_KEY as PENDING_PAYMENT_KEY } from './PaymentReturnScreen';
import { appPath } from '../routes/appPaths';

type YooCheckoutInstance = {
  render(containerId: string): Promise<void>;
  destroy(): void;
};

const WIDGET_SCRIPT = 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js';

export type PaymentWidgetLocationState = {
  confirmationToken: string;
  orderId: string;
  returnUrl: string;
};

function loadYooKassaWidgetScript(): Promise<void> {
  if (window.YooMoneyCheckoutWidget) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${WIDGET_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('script')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = WIDGET_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('script'));
    document.head.appendChild(s);
  });
}

export default function PaymentWidgetScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as PaymentWidgetLocationState | null;
  const [error, setError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    if (!state?.confirmationToken || !state?.returnUrl || !state?.orderId) {
      navigate(appPath('/home'), { replace: true });
      return;
    }
    sessionStorage.setItem(PENDING_PAYMENT_KEY, state.orderId);

    let cancelled = false;
    let widget: YooCheckoutInstance | null = null;

    (async () => {
      try {
        await loadYooKassaWidgetScript();
        if (cancelled) return;
        const Ctor = window.YooMoneyCheckoutWidget;
        if (!Ctor) {
          setError('Не удалось загрузить оплату');
          setInitializing(false);
          return;
        }
        widget = new Ctor({
          confirmation_token: state.confirmationToken,
          return_url: state.returnUrl,
          error_callback: () => setError('Ошибка виджета оплаты'),
        });
        await widget.render('theory-yookassa-mount');
        if (!cancelled) setInitializing(false);
      } catch {
        if (!cancelled) {
          setError('Не удалось открыть оплату');
          setInitializing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      widget?.destroy();
    };
  }, [state, navigate]);

  function handleBack() {
    navigate(-1);
  }

  if (!state?.confirmationToken) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f4f4f4' }}>
      <div className="max-w-md mx-auto w-full flex flex-col flex-1">
        <div className="flex items-center gap-3 px-4 pt-5 pb-3">
          <button
            type="button"
            onClick={handleBack}
            className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all active:scale-90"
            style={{
              background: 'rgba(255,255,255,0.9)',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
          >
            <ArrowLeft size={20} style={{ color: '#1a1a1a' }} />
          </button>
          <h1 className="text-lg font-bold" style={{ color: '#0a0a0a' }}>
            Оплата
          </h1>
        </div>

        {error && (
          <p className="px-4 text-sm text-center mb-2" style={{ color: '#c53030' }}>
            {error}
          </p>
        )}

        {initializing && (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Loader className="animate-spin" size={32} style={{ color: '#aaa' }} />
            <p className="text-sm" style={{ color: '#888' }}>
              Загрузка формы оплаты…
            </p>
          </div>
        )}

        <div
          id="theory-yookassa-mount"
          className="flex-1 px-2 pb-8 min-h-[320px] w-full max-w-full overflow-x-hidden"
        />
      </div>
    </div>
  );
}
